// Sprint CAL-1c — pagina «Attività» (/calendario/attivita): le attività dei calendari scelti, per tipo e per fascia
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import StickyHScroll from "@/components/StickyHScroll";
import { Intestazione } from "@/pages/crm/VistaRigheCrm";
import { Spiegabile, fmtTelefono, testoVeicolo } from "@/pages/crm/crmComuni";
import VehicleColumnsButton from "@/components/VehicleColumnsButton";
import { ChiudiAttivita, Rimanda } from "./ChiusuraAttivita";
import { MSG_CHIUDE } from "./TodoDialog";
import { fmtEvento, leggiLS, scriviLS } from "./calendarioComuni";

const TIPI = [["chiamata", "Richiami"], ["appuntamento", "Appuntamenti"], ["promemoria", "Promemoria"], ["todo", "To do List"]];
const FASCE = [["scadute", "Scadute", "bg-rose-50 text-rose-800"], ["oggi", "Oggi", "bg-blue-50 text-blue-800"],
  ["prossimi", "Prossimi giorni", "bg-slate-100 text-slate-700"], ["senza", "Senza scadenza", "bg-slate-100 text-slate-700"],
  ["fatte", "Fatte", "bg-slate-50 text-slate-500"]];
// Sprint CAL-2 — scelta e ordine con il pulsante «Colonne» delle liste; «Attività e nota» e «Segna fatto» sempre visibili
const COLONNE = [{ key: "quando", label: "Quando" }, { key: "attivita", label: "Attività e nota" }, { key: "nome", label: "Nome" },
  { key: "telefono", label: "Telefono" }, { key: "veicolo", label: "Veicolo" }, { key: "fonte", label: "Fonte" }, { key: "lead", label: "Lead" },
  { key: "dichi", label: "Di chi" }, { key: "fatto", label: "Segna fatto" }];
const SEMPRE = ["attivita", "fatto"];
const PARTENZA = { order: COLONNE.map((c) => c.key), visible: COLONNE.map((c) => c.key) };
const LARG = { quando: 150, attivita: 320, nome: 160, telefono: 140, veicolo: 200, fonte: 130, lead: 200, dichi: 160, fatto: 150 };
const LS_TIPI = "cal-att-tipi", LS_GIORNI = "cal-att-giorni", LS_FATTE = "cal-att-fatte", LS_LARG = "cal-att-larghezze", LS_COL = "cal-att-colonne";
const MSG_ALTRI = { sede: "Serve «Calendario: gestisci eventi di sede altrui»", personale: "La chiude il proprietario o chi l'ha assegnata" };

function Pillola({ attiva, onClick, testid, children }) {
  return <button type="button" onClick={onClick} aria-pressed={attiva} data-testid={testid}
    className={`rounded-full border px-3 py-1 text-xs transition-colors ${attiva ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{children}</button>;
}

function Contatore({ n, testo, colore, testid }) {
  return <div className={`rounded-lg border px-4 py-2 ${colore}`} data-testid={testid}><div className="text-2xl font-semibold leading-tight" data-testid={`${testid}-n`}>{n}</div>
    <div className="text-xs">{testo}</div></div>;
}

function ComandiRiga({ v, onChiudi, onCambio }) {
  if (v.fatto) return null;
  const motivo = v.tipo === "todo" && v.calendario === "sede" ? MSG_CHIUDE : MSG_ALTRI[v.calendario];
  const tondo = <button type="button" disabled={!v.puo_chiudere} onClick={() => onChiudi(v)} title="Segna fatto" data-testid={`cal-att-fatto-${v.id}`}
    className="h-7 w-7 rounded-full border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center justify-center"><Check className="h-4 w-4" /></button>;
  return (
    <div className="flex items-center gap-2">
      {v.puo_chiudere ? tondo : <Spiegabile motivo={motivo} testid={`cal-att-fatto-${v.id}-spiega`}>{tondo}</Spiegabile>}
      {v.puo_chiudere && ["scadute", "oggi"].includes(v.fascia) && <Rimanda ev={v} onFatto={onCambio} testid={`cal-att-rimanda-${v.id}`} />}
    </div>);
}

function Riga({ v, colonne, onChiudi, onCambio }) {
  return (
    <tr className={`border-b align-top ${v.fatto ? "text-slate-400" : ""}`} data-testid={`cal-att-riga-${v.id}`}>
      {colonne.map((c) => <td key={c.key} className="px-3 py-2 min-w-0 overflow-hidden text-ellipsis" data-testid={`cal-att-${c.key === "fatto" ? "comandi" : c.key}-${v.id}`}>
        {CELLE[c.key](v, onChiudi, onCambio)}</td>)}
    </tr>);
}

// Sprint CAL-2 — nome e telefono: dal contatto della lead (se la vedi) o dai campi dell'attività; Fonte e Veicolo solo per le collegate
const telefoni = (v) => (v.tipo === "todo" ? [] : v.contatto_lead ? v.contatto_lead.telefoni || [] : v.contatto_telefono ? [v.contatto_telefono] : []);
const CELLE = {
  quando: (v) => <span className="whitespace-nowrap">{v.inizio ? fmtEvento(v) : "—"}</span>,
  attivita: (v) => {
    const sotto = v.fatto ? `${v.esito || ""}${v.fatto_da_nome ? ` · chiusa da ${v.fatto_da_nome}` : ""}` : v.descrizione;
    return <>
      <Link to={`/calendario/attivita?evento=${v.id}`} className={`font-medium text-blue-700 hover:underline ${v.fatto ? "line-through text-slate-500" : ""}`}
        data-testid={`cal-att-titolo-${v.id}`}>{v.titolo}</Link>
      {sotto && <div className="text-xs text-slate-500 line-clamp-2 whitespace-pre-line" data-testid={`cal-att-nota-${v.id}`}>{sotto}</div>}</>;
  },
  nome: (v) => (v.tipo === "todo" ? "—" : (v.contatto_lead ? v.contatto_lead.nome : v.contatto_nome) || "—"),
  telefono: (v) => { const t = telefoni(v)[0]; return t ? <a href={`tel:${t}`} className="font-mono text-sky-700 hover:underline" data-testid={`cal-att-chiama-${v.id}`}>{fmtTelefono(t)}</a> : "—"; },
  veicolo: (v) => (v.opportunita_id ? testoVeicolo(v.opportunita_veicolo) : "—"),
  fonte: (v) => (v.opportunita_id ? v.opportunita_fonte || "—" : "—"),
  lead: (v) => v.opportunita_id && <Link to={`/crm/opportunita/${v.opportunita_id}`} className="text-blue-700 hover:underline">{v.opportunita_descrizione || "Lead"}</Link>,
  dichi: (v) => (v.calendario === "sede" ? v.proprietario_nome || `Sede ${v.sede_nome || ""}` : v.proprietario_nome),
  fatto: (v, onChiudi, onCambio) => <ComandiRiga v={v} onChiudi={onChiudi} onCambio={onCambio} />,
};

function Gruppo({ tipo, nome, voci, colonne, onChiudi, onCambio }) {
  const fasce = FASCE.filter(([k]) => k !== "senza" || tipo === "todo").map(([k, l, c]) => {
    const r = voci.filter((v) => v.fascia === k);
    if (k === "fatte") r.sort((a, b) => String(b.fatto_il || "").localeCompare(String(a.fatto_il || "")));
    return [k, l, c, r];
  }).filter((x) => x[3].length);
  if (!fasce.length) return null;
  return (
    <tbody data-testid={`cal-att-gruppo-${tipo}`}>
      <tr><td colSpan={colonne.length} className="px-3 pt-4 pb-1 text-sm font-semibold text-slate-800">{nome}</td></tr>
      {fasce.map(([k, l, c, r]) => (
        <React.Fragment key={k}>
          <tr data-testid={`cal-att-fascia-${tipo}-${k}`}><td colSpan={colonne.length} className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${c}`}>{l} ({r.length})</td></tr>
          {r.map((v) => <Riga key={v.id} v={v} colonne={colonne} onChiudi={onChiudi} onCambio={onCambio} />)}
        </React.Fragment>))}
    </tbody>);
}

export default function PaginaAttivita({ scelti, versione, onCambio }) {
  const [tipi, setTipiS] = useState(() => leggiLS(LS_TIPI, TIPI.map(([k]) => k)));
  const [giorni, setGiorniS] = useState(() => leggiLS(LS_GIORNI, 7));
  const [fatte, setFatteS] = useState(() => !!leggiLS(LS_FATTE, false));
  const [larg, setLargS] = useState(() => leggiLS(LS_LARG, {}));
  const [cfg, setCfgS] = useState(() => leggiLS(LS_COL, PARTENZA));
  const setCfg = (c) => { const n = { order: c.order, visible: [...new Set([...c.visible, ...SEMPRE])] }; setCfgS(n); scriviLS(LS_COL, n); };
  const perKey = Object.fromEntries(COLONNE.map((c) => [c.key, c]));
  const ordine = [...cfg.order.filter((k) => perKey[k]), ...COLONNE.map((c) => c.key).filter((k) => !cfg.order.includes(k))];
  const colonne = ordine.filter((k) => cfg.visible.includes(k) || SEMPRE.includes(k)).map((k) => perKey[k]);
  const [voci, setVoci] = useState([]);
  const [chiudi, setChiudi] = useState(null);
  const carica = useCallback(() => {
    if (!scelti.length) { setVoci([]); return; }
    api.get(`/calendario/attivita?${new URLSearchParams({ calendari: scelti.join(","), giorni: String(giorni), fatte: String(fatte) })}`)
      .then((r) => setVoci(r.data || [])).catch(() => setVoci([]));
  }, [scelti, giorni, fatte]);
  useEffect(() => { carica(); }, [carica, versione]);
  const cambio = () => { carica(); onCambio?.(); };
  const tipo = (k) => { const n = tipi.includes(k) ? tipi.filter((x) => x !== k) : [...tipi, k]; if (n.length) { setTipiS(n); scriviLS(LS_TIPI, n); } };
  const mostrate = voci.filter((v) => tipi.includes(v.tipo));
  const conta = (f) => mostrate.filter((v) => v.fascia === f).length;
  return (
    <div className="space-y-3" data-testid="cal-attivita">
      <div className="flex flex-wrap items-center gap-2">
        {TIPI.map(([k, l]) => <Pillola key={k} attiva={tipi.includes(k)} onClick={() => tipo(k)} testid={`cal-att-tipo-${k}`}>{l}</Pillola>)}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        {[7, 14, 30].map((n) => <Pillola key={n} attiva={giorni === n} onClick={() => { setGiorniS(n); scriviLS(LS_GIORNI, n); }}
          testid={`cal-att-giorni-${n}`}>Prossimi {n} giorni</Pillola>)}
        <label className="flex items-center gap-2 text-sm text-slate-600 ml-1"><input type="checkbox" checked={fatte} data-testid="cal-att-fatte"
          onChange={(e) => { setFatteS(e.target.checked); scriviLS(LS_FATTE, e.target.checked); }} /> Mostra anche le fatte</label>
      </div>
      <div className="flex flex-wrap gap-3">
        <Contatore n={conta("scadute")} testo="Scadute" colore="border-rose-200 bg-rose-50 text-rose-800" testid="cal-att-conta-scadute" />
        <Contatore n={conta("oggi")} testo="Oggi" colore="border-blue-200 bg-blue-50 text-blue-800" testid="cal-att-conta-oggi" />
        <Contatore n={conta("prossimi")} testo={`Prossimi ${giorni} giorni`} colore="border-slate-200 bg-slate-50 text-slate-700" testid="cal-att-conta-prossimi" />
      </div>
      <div className="flex justify-end">
        <VehicleColumnsButton allColumns={COLONNE} order={ordine} visible={colonne.map((c) => c.key)} onChange={setCfg}
          onReset={() => setCfg(PARTENZA)} onResetWidths={() => { setLargS({}); scriviLS(LS_LARG, {}); }} />
      </div>
      <StickyHScroll testid="cal-att-scroll">
        <table className="text-sm" style={{ tableLayout: "fixed", width: colonne.reduce((s, c) => s + (larg[c.key] ?? LARG[c.key]), 0) }} data-testid="cal-att-tabella">
          <thead className="bg-slate-50 border-b"><tr className="text-left text-[12px] text-slate-600 uppercase tracking-wide">
            {colonne.map((c) => <Intestazione key={c.key} c={c} w={larg[c.key] ?? LARG[c.key]} sort={{ by: null }} onSort={() => {}}
              onLarg={(k, w) => { const n = { ...larg, [k]: Math.round(w) }; setLargS(n); scriviLS(LS_LARG, n); }} />)}
          </tr></thead>
          {TIPI.filter(([k]) => tipi.includes(k)).map(([k, l]) => <Gruppo key={k} tipo={k} nome={l} voci={mostrate.filter((v) => v.tipo === k)}
            colonne={colonne} onChiudi={setChiudi} onCambio={cambio} />)}
        </table>
        {!mostrate.length && <div className="py-8 text-center text-sm text-slate-400" data-testid="cal-att-vuota">Nessuna attività</div>}
      </StickyHScroll>
      {chiudi && <ChiudiAttivita ev={chiudi} onChiudi={() => setChiudi(null)} onFatto={() => { setChiudi(null); cambio(); }} />}
    </div>
  );
}
